import { useState, useEffect, useRef } from "react";
import { ArrowRight, Menu, X, Instagram, Linkedin, Twitter, ChevronLeft, ChevronRight, Check } from "lucide-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBrain, faGears, faEye, faMicrophone,
  faBolt, faStar, faChartLine,
  faMagnifyingGlass, faSliders, faRocket, faHeart,
  faBell, faUtensils, faLeaf,
  faArrowRight as faArR,
} from "@fortawesome/free-solid-svg-icons";
import Logo from "/Logo.png";
import "./BotsPage.css";
import "./shared-dark.css";

const navLinks = [
  { label:"About",   href:"/about"   },
  { label:"Agents",  href:"/bots"    },
  { label:"Hotel",   href:"/hotel"   },
  { label:"Contact", href:"/about" },
];

const heroStats = [
  { value:"3",      label:"Core Agents",            note:"Hotel · Restaurant · Clinic" },
  { value:"1,200+", label:"Weekly Interactions",     note:"Every message refined into an experience" },
  { value:"99.4%",  label:"Response Accuracy",       note:"Tone, timing, and intent understood instantly" },
];

const benefits = [
  { icon: faBrain,       title:"Emotional Intelligence", desc:"Understands emotion and intent in real-time communication, going beyond keywords to feel what guests truly need." },
  { icon: faGears,       title:"Operational Ease",       desc:"Manages bookings, inquiries, and service requests seamlessly — so your staff never drops a request again." },
  { icon: faEye,         title:"Predictive Empathy",     desc:"Learns from guest behavior to anticipate and fulfill needs before they're even expressed." },
  { icon: faMicrophone,  title:"Brand Voice",            desc:"Speaks in your brand's tone — refined, human, and distinct — every single time." },
];

const agents = [
  {
    tag:"Hotels", color:"#c9a84c", bg:"rgba(201,168,76,.08)", border:"rgba(201,168,76,.2)",
    icon: faBell,
    name:"EVA", role:"Hotel Concierge Agent",
    desc:"Your all-in-one guest relations agent. Handles check-ins, room service, and post-stay feedback — all via WhatsApp.",
    bullets:["Connected to PMS & dashboards","Personalized guest recognition","Instant room service & requests"],
    cta:"Meet EVA",
  },
  {
    tag:"Restaurants", color:"#10b981", bg:"rgba(16,185,129,.08)", border:"rgba(16,185,129,.2)",
    icon: faUtensils,
    name:"DINE", role:"Dining & Reservations Agent",
    desc:"Your restaurant's AI-powered table manager. Handles reservations, menu automation, and guest communication — all through WhatsApp.",
    bullets:["WhatsApp reservations & orders","Tracks wait time and service quality","Real-time menu & upsell automation"],
    cta:"Meet DINE",
  },
  {
    tag:"Clinics & Spas", color:"#818cf8", bg:"rgba(129,140,248,.08)", border:"rgba(129,140,248,.2)",
    icon: faLeaf,
    name:"SERENE", role:"Wellness & Appointment Agent",
    desc:"AI-powered scheduling, client follow-ups, and loyalty management for wellness brands.",
    bullets:["Automated booking reminders","Personalized post-session follow-ups","Loyalty & retention intelligence"],
    cta:"Meet SERENE",
  },
];

const whyCards = [
  { icon: faBolt,       title:"Intelligent Efficiency",   desc:"Streamlines operations — from WhatsApp inquiries to dashboard insights — reducing workload while increasing precision." },
  { icon: faStar,       title:"Consistent Brilliance",    desc:"Ensures your brand's tone, warmth, and elegance never waver — every interaction feels authentically you." },
  { icon: faChartLine,  title:"Data that Refines Luxury", desc:"Each conversation becomes insight — transforming guest behavior into loyalty, personalization, and brand reputation." },
];

const steps = [
  { num:"01", icon: faMagnifyingGlass, title:"Discover",    desc:"Guests connect via WhatsApp to explore rooms, availability, or dining — anywhere, anytime." },
  { num:"02", icon: faSliders,         title:"Personalize", desc:"AI Squared tailors experiences automatically — remembering preferences and anticipating needs." },
  { num:"03", icon: faRocket,          title:"Elevate",     desc:"Guests enjoy intelligent service, orders, spa bookings, and feedback handled with grace and precision." },
  { num:"04", icon: faHeart,           title:"Remember",    desc:"AI Squared continues the relationship — sending thank yous, reviews, and warm 'We miss you' notes." },
];

const testimonials = [
  { quote:"AI Squared completely transformed how our guests book and interact. The WhatsApp concierge feels so natural — guests say it's like texting a real human.", name:"Amira A.", role:"Operations Director", company:"Sam's Story Hotels" },
  { quote:"Our restaurant reservations doubled since integrating AI Squared. It handles messages instantly and remembers guests' preferences perfectly.", name:"Karim E.", role:"General Manager", company:"Oasis Fine Dining" },
  { quote:"AI Squared is the assistant we didn't know we needed — it saved our team hours each day while keeping our clients happier than ever.", name:"Nadia R.", role:"Founder", company:"Serene Wellness Clinics" },
];

export default function BotsPage() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [tIdx, setTIdx] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const advance = () => setTIdx(i => (i + 1) % testimonials.length);
  const retreat = () => setTIdx(i => (i - 1 + testimonials.length) % testimonials.length);

  const resetTimer = (fn: () => void) => {
    if (timerRef.current) clearInterval(timerRef.current);
    fn();
    timerRef.current = setInterval(advance, 6000);
  };

  useEffect(() => {
    timerRef.current = setInterval(advance, 6000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, []);

  useEffect(() => {
    const obs = new IntersectionObserver(
      es => es.forEach(e => { if (e.isIntersecting) { e.target.classList.add("hn-in"); obs.unobserve(e.target); } }),
      { threshold: 0.08 }
    );
    document.querySelectorAll<HTMLElement>(".hn-up").forEach(el => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  return (
    <div className="hn bt-wrap">

      {/* ── NAV ── */}
      <nav className="hn-nav">
        <div className="hn-nav-wrap">
          <a href="/" className="hn-logo"><img src={Logo} alt="AI²"/></a>
          <div className="hn-nav-links">{navLinks.map(l => <a key={l.href} href={l.href}>{l.label}</a>)}</div>
          <div className="hn-nav-end">
            <a href="/hotel"            className="hn-nav-demo">Book a Demo</a>
            <a href="/dashboard/login" className="hn-nav-login">Staff Login →</a>
            <button className="hn-burger" onClick={() => setMobileOpen(v => !v)}>
              {mobileOpen ? <X size={20}/> : <Menu size={20}/>}
            </button>
          </div>
        </div>
        {mobileOpen && (
          <div className="hn-mob">
            {navLinks.map(l => <a key={l.href} href={l.href} onClick={() => setMobileOpen(false)}>{l.label}</a>)}
            <a href="/hotel" onClick={() => setMobileOpen(false)}>Book a Demo</a>
            <a href="/dashboard/login" onClick={() => setMobileOpen(false)} className="hn-mob-login">Staff Login →</a>
          </div>
        )}
      </nav>

      {/* ══════════════════════════════════════
          HERO
      ══════════════════════════════════════ */}
      <section className="bt-hero">
        <div className="bt-grid-bg"/>
        <div className="bt-glow bt-glow-a"/>
        <div className="bt-glow bt-glow-b"/>
        <div className="bt-watermark">AI²</div>

        <div className="bt-hero-inner">
          <div className="bt-hero-left hn-up">
            <div className="hn-badge"><span className="hn-badge-dot"/>Hospitality AI · Intelligent Agents</div>
            <h1 className="bt-h1">
              Where Intelligence<br/>
              <span className="bt-h1-gold">Meets Experience.</span>
            </h1>
            <p className="bt-hero-p">
              Our Agents don't just automate — they understand, anticipate, and elevate every guest interaction,
              blending data with emotion to create experiences that feel effortlessly personal.
            </p>
            <div className="bt-hero-actions">
              <a href="/agents" className="hn-btn">Explore Agents <ArrowRight size={14}/></a>
              <a href="/hotel"   className="hn-btn-ghost">Book a Demo</a>
            </div>
          </div>

          <div className="bt-hero-right hn-up" style={{transitionDelay:"0.12s"}}>
            {heroStats.map((s, i) => (
              <div key={i} className="bt-hero-card" style={{transitionDelay:`${0.18 + i * 0.1}s`}}>
                <div className="bt-hero-card-val">{s.value}</div>
                <div className="bt-hero-card-label">{s.label}</div>
                <div className="bt-hero-card-note">{s.note}</div>
              </div>
            ))}
            <div className="bt-hero-pill">
              <span className="bt-hero-pill-dot"/>
              Guests · Managers · Concierge AI
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════
          WHAT ARE AGENTS
      ══════════════════════════════════════ */}
      <section className="bt-what">
        <div className="bt-what-inner">
          <div className="bt-sec-hd hn-up">
            <div className="hn-eyebrow">The Intelligence</div>
            <h2 className="bt-h2">What Are AI² Agents?</h2>
            <p className="bt-sec-sub">
              AI Squared Agents mirror your brand's personality — precise, empathetic, and effortless.
              They understand, adapt, and respond with intention, ensuring every guest feels <em>remembered</em>, not managed.
            </p>
          </div>
          <div className="bt-benefits-grid">
            {benefits.map((b, i) => (
              <div key={i} className="bt-benefit hn-up" style={{transitionDelay:`${i * 0.08}s`}}>
                <div className="bt-benefit-icon">
                  <FontAwesomeIcon icon={b.icon}/>
                </div>
                <h3 className="bt-benefit-title">{b.title}</h3>
                <p className="bt-benefit-desc">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════
          FOR GUESTS / FOR BUSINESSES
      ══════════════════════════════════════ */}
      <section className="bt-split">
        <div className="bt-grid-bg"/>
        <div className="bt-glow bt-glow-c"/>
        <div className="bt-split-inner">
          <div className="bt-sec-hd bt-sec-hd-light hn-up">
            <div className="hn-eyebrow" style={{color:"rgba(201,168,76,.75)"}}>Luxury, Reimagined</div>
            <h2 className="bt-h2-light">Through Automation.</h2>
            <p className="bt-sec-sub bt-sec-sub-light">
              We build intelligent systems that elevate every moment — from guest check-in to post-stay feedback.
            </p>
          </div>
          <div className="bt-split-cols">
            <div className="bt-split-col hn-up">
              <div className="bt-split-tag" style={{color:"#c9a84c",background:"rgba(201,168,76,.08)",borderColor:"rgba(201,168,76,.2)"}}>For Guests</div>
              <h3 className="bt-split-h">Your stay, your way.<br/><span>Simplified through AI.</span></h3>
              <ul className="bt-split-list">
                {["Check in, order room service, or book a table directly on WhatsApp",
                  "Receive personalized recommendations and VIP recognition",
                  "Enjoy instant service, feedback collection, and tailored offers",
                ].map((item, j) => (
                  <li key={j}>
                    <span className="bt-split-check" style={{background:"rgba(201,168,76,.12)",color:"#c9a84c"}}><Check size={10} strokeWidth={3}/></span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <a href="/hotel" className="bt-split-link" style={{color:"#c9a84c"}}>Explore Dashboard <FontAwesomeIcon icon={faArR} style={{fontSize:11}}/></a>
            </div>
            <div className="bt-split-divider"/>
            <div className="bt-split-col hn-up" style={{transitionDelay:"0.12s"}}>
              <div className="bt-split-tag" style={{color:"#818cf8",background:"rgba(129,140,248,.08)",borderColor:"rgba(129,140,248,.2)"}}>For Businesses</div>
              <h3 className="bt-split-h">Intelligence that<br/><span>listens, learns, and acts.</span></h3>
              <ul className="bt-split-list">
                {["Unified dashboards for all automations and analytics",
                  "Real-time coordination between agents and staff",
                  "Predictive insights integrated with PMS & CRM",
                ].map((item, j) => (
                  <li key={j}>
                    <span className="bt-split-check" style={{background:"rgba(129,140,248,.12)",color:"#818cf8"}}><Check size={10} strokeWidth={3}/></span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <a href="/hotel" className="bt-split-link" style={{color:"#818cf8"}}>Explore Dashboard <FontAwesomeIcon icon={faArR} style={{fontSize:11}}/></a>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════
          AGENT CARDS
      ══════════════════════════════════════ */}
      <section className="bt-agents">
        <div className="bt-agents-inner">
          <div className="bt-sec-hd hn-up">
            <div className="hn-eyebrow">The AI² Agents</div>
            <h2 className="bt-h2">Every Experience,<br/><span style={{color:"#c9a84c"}}>Powered by an Agent.</span></h2>
            <p className="bt-sec-sub">
              Designed for precision and empathy — each agent speaks your industry's language fluently.
            </p>
          </div>
          <div className="bt-agent-grid">
            {agents.map((a, i) => (
              <div key={a.name} className="bt-acard hn-up" style={{transitionDelay:`${i * 0.1}s`}}>
                <div className="bt-acard-stripe" style={{background:a.color}}/>
                <div className="bt-acard-body">
                  <div className="bt-acard-top">
                    <div className="bt-acard-icon-wrap" style={{background:a.bg,border:`1px solid ${a.border}`}}>
                      <FontAwesomeIcon icon={a.icon} style={{color:a.color,fontSize:16}}/>
                    </div>
                    <span className="bt-acard-tag" style={{color:a.color,background:a.bg,borderColor:a.border}}>{a.tag}</span>
                  </div>
                  <div className="bt-acard-name">{a.name}</div>
                  <div className="bt-acard-role">{a.role}</div>
                  <p className="bt-acard-desc">{a.desc}</p>
                  <div className="bt-acard-sep"/>
                  <ul className="bt-acard-bullets">
                    {a.bullets.map((b, j) => (
                      <li key={j}>
                        <span className="bt-acard-check" style={{background:a.bg,borderColor:a.border}}>
                          <FontAwesomeIcon icon={faArR} style={{color:a.color,fontSize:8}}/>
                        </span>
                        {b}
                      </li>
                    ))}
                  </ul>
                  <a href="/agents" className="bt-acard-cta" style={{color:a.color,background:a.bg,borderColor:a.border}}>
                    {a.cta} <ArrowRight size={12}/>
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════
          WHY THEY MATTER
      ══════════════════════════════════════ */}
      <section className="bt-why">
        <div className="bt-grid-bg"/>
        <div className="bt-glow bt-glow-d"/>
        <div className="bt-why-inner">
          <div className="bt-why-left hn-up">
            <div className="hn-eyebrow" style={{color:"rgba(201,168,76,.75)"}}>Why They Matter</div>
            <h2 className="bt-h2-light">Luxury is in<br/>the Details.</h2>
            <p className="bt-sec-sub bt-sec-sub-light">
              AI Squared Agents handle the unseen details — from instant messages to smart dashboards —
              freeing your team to focus on what defines true luxury: the human touch.
            </p>
            <blockquote className="bt-why-quote">
              "True luxury isn't loud — it's felt in the perfection of every moment."
            </blockquote>
          </div>
          <div className="bt-why-right">
            {whyCards.map((w, i) => (
              <div key={i} className="bt-why-card hn-up" style={{transitionDelay:`${i * 0.1}s`}}>
                <div className="bt-why-icon">
                  <FontAwesomeIcon icon={w.icon}/>
                </div>
                <div>
                  <h3 className="bt-why-title">{w.title}</h3>
                  <p className="bt-why-desc">{w.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════
          FRAMEWORK
      ══════════════════════════════════════ */}
      <section className="bt-framework">
        <div className="bt-framework-inner">
          <div className="bt-sec-hd hn-up">
            <div className="hn-eyebrow">Our Proven Framework</div>
            <h2 className="bt-h2">For Effortless Luxury.</h2>
            <p className="bt-sec-sub">
              From the first message to the final goodbye, every interaction feels curated — personalized, seamless, and human.
            </p>
          </div>
          <div className="bt-steps">
            {steps.map((s, i) => (
              <div key={s.num} className="bt-step hn-up" style={{transitionDelay:`${i * 0.09}s`}}>
                <div className="bt-step-num">{s.num}</div>
                <div className="bt-step-icon">
                  <FontAwesomeIcon icon={s.icon}/>
                </div>
                <div className="bt-step-bar"/>
                <h3 className="bt-step-title">{s.title}</h3>
                <p className="bt-step-desc">{s.desc}</p>
                {i < steps.length - 1 && (
                  <div className="bt-step-connector">
                    <ArrowRight size={14}/>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════
          TESTIMONIALS
      ══════════════════════════════════════ */}
      <section className="bt-testi">
        <div className="bt-grid-bg"/>
        <div className="bt-glow bt-glow-e"/>
        <div className="bt-testi-inner">
          <div className="bt-sec-hd bt-sec-hd-light bt-sec-hd-center hn-up">
            <div className="hn-eyebrow" style={{color:"rgba(201,168,76,.75)",justifyContent:"center"}}>Loved By Brands</div>
            <h2 className="bt-h2-light" style={{textAlign:"center"}}>Hotels, Restaurants & Clinics<br/><span style={{color:"#c9a84c"}}>That Redefine Hospitality.</span></h2>
            <p className="bt-sec-sub bt-sec-sub-light" style={{textAlign:"center",margin:"0 auto"}}>Real stories from visionary brands using AI Squared every day.</p>
          </div>

          <div className="bt-testi-track hn-up">
            <button className="bt-arr bt-arr-prev" onClick={() => resetTimer(retreat)} aria-label="Previous">
              <ChevronLeft size={18}/>
            </button>
            <div className="bt-tcard-viewport">
              {testimonials.map((t, i) => (
                <div key={i} className={`bt-tcard${i === tIdx ? " active" : ""}`}>
                  <div className="bt-tcard-stars">
                    {[...Array(5)].map((_,j) => <span key={j} className="bt-star"><FontAwesomeIcon icon={faStar}/></span>)}
                  </div>
                  <p className="bt-tcard-quote">"{t.quote}"</p>
                  <div className="bt-tcard-footer">
                    <div className="bt-tcard-av">{t.name[0]}</div>
                    <div>
                      <div className="bt-tcard-name">{t.name}</div>
                      <div className="bt-tcard-role">{t.role} · {t.company}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <button className="bt-arr bt-arr-next" onClick={() => resetTimer(advance)} aria-label="Next">
              <ChevronRight size={18}/>
            </button>
          </div>

          <div className="bt-dots">
            {testimonials.map((_,i) => (
              <button key={i} className={`bt-dot${i === tIdx ? " active" : ""}`}
                onClick={() => resetTimer(() => setTIdx(i))}/>
            ))}
          </div>

          <p className="bt-testi-foot hn-up">
            Join <strong>200+ hospitality leaders</strong> shaping the future of intelligent luxury.
          </p>
        </div>
      </section>

      {/* ══════════════════════════════════════
          CTA
      ══════════════════════════════════════ */}
      <section className="hn-cta">
        <div className="hn-cta-ring hn-cta-r1"/><div className="hn-cta-ring hn-cta-r2"/><div className="hn-cta-ring hn-cta-r3"/>
        <div className="hn-cta-glow"/>
        <div className="hn-cta-wrap">
          <div className="hn-eyebrow hn-up" style={{color:"#c9a84c",justifyContent:"center"}}>Let's Talk Luxury</div>
          <h2 className="hn-cta-h2 hn-up">Transform Your<br/>Guest Experience.</h2>
          <p className="hn-cta-sub hn-up">Let's automate your brand's next chapter — live in 7 days.</p>
          <div className="hn-cta-btns hn-up">
            <a href="/agents" className="hn-btn hn-btn-cta">Explore Agents <ArrowRight size={14}/></a>
            <a href="/hotel"   className="hn-btn-ghost hn-btn-ghost-lt">Book a Demo</a>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════
          FOOTER
      ══════════════════════════════════════ */}
      <footer className="hn-footer">
        <div className="hn-footer-wrap">
          <div className="hn-footer-brand">
            <img src={Logo} alt="AI Squared" className="hn-footer-logo"/>
            <p>Intelligent hospitality experiences for hotels, restaurants, spas, and wellness brands worldwide.</p>
            <div className="hn-footer-socials">
              <a href="#" className="hn-social" aria-label="Instagram"><Instagram size={15}/></a>
              <a href="#" className="hn-social" aria-label="LinkedIn"><Linkedin size={15}/></a>
              <a href="#" className="hn-social" aria-label="Twitter"><Twitter size={15}/></a>
            </div>
          </div>
          {[
            {h:"Product", ls:[["Agents","/bots"],["Hotel Dashboard","/hotel"],["Integrations","/about"],["Pricing","/hotel"]]},
            {h:"Company", ls:[["About","/about"],["Contact","/about"],["Book a Demo","/hotel"],["Careers","#"]]},
            {h:"Sectors", ls:[["Hotels & Resorts","/hotel"],["Restaurants","/bots"],["Spas & Wellness","/bots"],["Clinics","/bots"]]},
          ].map(col => (
            <div key={col.h} className="hn-fcol">
              <div className="hn-fcol-h">{col.h}</div>
              {col.ls.map(([l,h]) => <a key={l} href={h}>{l}</a>)}
            </div>
          ))}
        </div>
        <div className="hn-footer-btm">
          <span>© {new Date().getFullYear()} AI Squared. All rights reserved.</span>
          <div><a href="/privacy">Privacy</a><a href="/terms">Terms</a></div>
        </div>
      </footer>

    </div>
  );
}
